/**
 * Feature flags controlled by environment variables.
 *
 * - `flightsEnabled`: master switch for the flights module.
 *   In production we can still gate it until every carrier is stable.
 *   In testing/local it stays on.
 * - `showFlightsBetaBanner`: the "Módulo en construcción" notice visible to
 *   external users. Must be removable independently of the module itself so
 *   we can turn the banner off once the experience is ready, without having
 *   to disable flights entirely. Defaults to `true` while we stabilize.
 * - `carsEnabled` / `busesEnabled`: mismo criterio que `flightsEnabled` — se
 *   ocultan por default en producción hasta que esos módulos estén listos.
 */
// Sólo 'true'/'false' explícitos pisan el default; cualquier otro valor ('0',
// '1', vacío, no seteado) cae al default. Evita que 'VITE_X_ENABLED=0' —lo
// obvio para "apagado" si no leíste este archivo— termine prendiendo el flag
// ('0' es truthy como string).
function resolveFlag(value: string | undefined, defaultValue: boolean): boolean {
	if (value === 'true') return true
	if (value === 'false') return false
	return defaultValue
}

export function useFeatureFlags() {
	// Si el flag está seteado explícitamente, se respeta ('true' on / 'false' off).
	// Si NO está seteado, el default es SEGURO: habilitado en todos lados EXCEPTO en el
	// build de producción (import.meta.env.MODE === 'production'). Así el módulo queda
	// oculto en prod por default y libre en testing/local, sin depender de editar el
	// .env de prod. Para lanzarlo en prod, poner VITE_<MODULO>_ENABLED=true.
	const isNonProdDefault = import.meta.env.MODE !== 'production'

	const flightsEnabled = resolveFlag(
		import.meta.env.VITE_FLIGHTS_ENABLED,
		isNonProdDefault
	)
	const carsEnabled = resolveFlag(
		import.meta.env.VITE_CARS_ENABLED,
		isNonProdDefault
	)
	const busesEnabled = resolveFlag(
		import.meta.env.VITE_BUSES_ENABLED,
		isNonProdDefault
	)

	const showFlightsBetaBanner =
		import.meta.env.VITE_FLIGHTS_BETA_BANNER !== 'false'

	return {
		flightsEnabled,
		carsEnabled,
		busesEnabled,
		showFlightsBetaBanner,
	}
}
